import {
  SPARTAN_ENGINE_DISPLAY_LABELS,
  SPARTAN_PRODUCT_ENGINE_SPEC_LABELS,
  SPARTAN_PRODUCT_ENGINE_SPEC_UNITS,
} from '@/app/lcv/components/sections/spartan/products/engine/constants';
import type {
  SpartanProductEngineContent,
  SpartanProductEngineContentInput,
  SpartanProductEngineData,
  SpartanProductEngineItem,
  SpartanProductEngineLogo,
  SpartanProductEngineProps,
} from '@/app/lcv/components/sections/spartan/products/engine/types';
import type {
  SpartanEngineCmsData,
  SpartanEngineKey,
} from '@/services/spartan/types';

export const SPARTAN_PRODUCT_ENGINE_KEYS = [
  '2.2 Biturbo',
  '2.2',
  '3.0',
] as const satisfies readonly SpartanEngineKey[];

export const getEngineLogoMobileSrc = (
  logo: SpartanProductEngineLogo
): string => logo.mobileSrc ?? logo.src;

export const getEngineLogoPreloadSrcs = (
  logo: SpartanProductEngineLogo
): string[] => {
  const desktopSrc = logo.src;
  const mobileSrc = getEngineLogoMobileSrc(logo);

  return desktopSrc === mobileSrc ? [desktopSrc] : [desktopSrc, mobileSrc];
};

export const getEngineLogoIdentity = (logo: SpartanProductEngineLogo): string =>
  [logo.src, logo.mobileSrc ?? ''].join('\0');

export const getEnginePreloadAssetCount = (
  engine: SpartanProductEngineItem
): number =>
  getEngineLogoPreloadSrcs(engine.logo).length +
  (engine.stageMedia?.image.src ? 1 : 0);

export const getEngineMediaKey = (engine: SpartanProductEngineItem): string =>
  [
    engine.engineKey,
    ...getEngineLogoPreloadSrcs(engine.logo),
    engine.stageMedia?.image.src,
    engine.stageMedia?.video?.src,
  ]
    .filter(Boolean)
    .join(':');

const toParagraphHtml = (content: string): string =>
  content.startsWith('<p>') ? content : `<p>${content}</p>`;

export const createSpartanEngineContent = ({
  panelRemark,
  remark,
  ...content
}: SpartanProductEngineContentInput): SpartanProductEngineContent => ({
  ...content,
  ...(panelRemark
    ? {
        panelRemark: toParagraphHtml(panelRemark),
      }
    : {}),
  remark: toParagraphHtml(remark),
});

const buildEngineSpecs = (
  content: SpartanProductEngineContent,
  cmsEngine?: SpartanEngineCmsData
) => [
  ...(cmsEngine?.horsepower != null
    ? [
        {
          label: SPARTAN_PRODUCT_ENGINE_SPEC_LABELS.horsepower,
          value: cmsEngine.horsepower,
          unit: SPARTAN_PRODUCT_ENGINE_SPEC_UNITS.horsepower,
        },
      ]
    : []),
  ...(cmsEngine?.torque != null
    ? [
        {
          label: SPARTAN_PRODUCT_ENGINE_SPEC_LABELS.torque,
          value: cmsEngine.torque,
          unit: SPARTAN_PRODUCT_ENGINE_SPEC_UNITS.torque,
        },
      ]
    : []),
  {
    label: SPARTAN_PRODUCT_ENGINE_SPEC_LABELS.fuelEconomy,
    value: content.fuelEconomy,
    unit: SPARTAN_PRODUCT_ENGINE_SPEC_UNITS.fuelEconomy,
  },
];

export const buildSpartanProductEngineProps = (
  data: SpartanProductEngineData,
  cmsEngines: Partial<Record<SpartanEngineKey, SpartanEngineCmsData>>
): SpartanProductEngineProps => ({
  title: data.title,
  cta: data.cta,
  theme: data.theme,
  engines: SPARTAN_PRODUCT_ENGINE_KEYS.flatMap((engineKey) => {
    const content = data.engines[engineKey];
    const cmsEngine = cmsEngines[engineKey];
    if (!content || !cmsEngine) return [];
    const { tabLabel, heroLabel } = SPARTAN_ENGINE_DISPLAY_LABELS[engineKey];

    return [
      {
        ...content,
        engineKey,
        tabLabel,
        heroLabel,
        ...(cmsEngine?.horsepower != null
          ? { horsepower: cmsEngine.horsepower }
          : {}),
        ...(cmsEngine?.torque != null ? { torque: cmsEngine.torque } : {}),
        specs: buildEngineSpecs(content, cmsEngine),
      },
    ];
  }),
});
